import { DataType } from '@mode-switch/express';
import { Attr, Names, Values } from '@mode-switch/model';
import { QueryResult } from '@mode-switch/query';
import {
  EncField,
  EncSeries,
  EncSort,
  Encoding,
  MarkChannel,
  MarksSummary,
  SortType,
  VariableType,
} from '@mode-vegas/vegas';
import {
  EncodingHelpers,
  FieldFormatHelpers,
  RenderDataModel,
  SUBTOTAL_TOKEN,
  SchemaModel,
  SharedAxisHelpers,
  TOTALS_TOKEN,
} from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { muze } from './muze';
import { VisualizationDataStats } from './muze-chart-renderer';
import { Muze } from './muze-types';

export const SID_FIELD = '$SID';

export type MuzeDataFormatFn = (dataInfo: MuzeTextDataInfo, contextInfo: MuzeContextInfo) => string;
export type MuzeGetRowDataFn = () => any;
export type MuzeDatasetTransformFn = (dataset: any) => any;
export type MuzeTextFilterFn = (dataInfo: MuzeTextDataInfo, contextInfo: MuzeContextInfo) => boolean;
export type SubtotalFormatFn = (contextInfo: MuzeContextInfo) => string;

export interface MuzeDataField {
  readonly name: string;
  readonly type: 'measure' | 'dimension';
  readonly source: Attr.Source.Field | Attr.Source.Names | Attr.Source.Values; // Used internally by Mode only. Muze ignores it.
  readonly as?: 'continuous' | 'discrete';
  readonly subtype?: 'categorical' | 'continuous' | 'temporal' | 'binned';
  readonly binSize?: number | void;
  readonly format?: string | MuzeDataFormatFn;
  readonly displayName?: string;
}

export interface MuzeFieldConfig {
  field: string;
  as?: 'discrete' | 'continuous';
}

export type MuzeDataSchema = ReadonlyArray<MuzeDataField>;

export interface MuzeDataItem {
  [key: string]: any;
}

export interface MuzeDatasetTransform {
  [key: string]: MuzeDatasetTransformFn;
}

export type MuzeDataset = ReadonlyArray<MuzeDataItem>;

export interface MuzeChartData {
  readonly data: MuzeDataset;
  readonly schema: MuzeDataSchema;
  readonly dataProperties: MuzeDataProperties;
}

export interface MuzeDataModel {
  dispose(): void;
  getParent(): MuzeDataModel | undefined;
  select(filter: any): MuzeDataModel;
  groupBy(fields: Array<string | undefined>, reducers: any[]): MuzeDataModel;
  getDataMeta(): { rows: number };
  getData(): { data: any[][] };
}

export interface DataSchemaStats {
  dataCount: number;
  schemaCount: number;
  dataProperties: MuzeDataProperties;
}

interface MuzeFilterCondition {
  field: string;
  value: any;
  operator: any;
}

export interface MuzeChartSeries {
  dataModel: MuzeDataModel;
  schema: MuzeDataSchema;
  transform?: MuzeDatasetTransform;
  stats: DataSchemaStats;
}

export interface MuzeDataProperties {
  discreteCount: { [seriesId: string]: number };
  dataLength: number;
  omittedNullCount?: number;
  loadTime?: number;
}

export interface MarkSeriesRef {
  id?: string;
  referenceId?: string;
}

export interface SeriesMarkSummary {
  readonly series: string;
  readonly unique: EncField[];
  readonly shared: MarkSeriesRef[];
}

export interface MuzeChannelEncoding {
  value?: (() => void) | number;
  field?: string | undefined;
  as?: Lowercase<VariableType.DISCRETE> | Lowercase<VariableType.CONTINUOUS>;
  formatter?: MuzeDataFormatFn | undefined;
  rotation?: number | string | null;
  range?: (range: number[]) => number[];
  filter?: MuzeTextFilterFn | undefined;
  removeCollision?: boolean;
  showStackSum?: boolean;
}

// Muze callback objects
export interface MuzeTextDataInfo {
  rawValue: any;
  formattedValue?: string;
  getRowData?: MuzeGetRowDataFn;
  getStackSumData?: MuzeGetRowDataFn;
  rowData?: any;
}

export interface MuzeTooltipDataInfo {
  dataModel: any;
}

export interface MuzeContextInfo {
  context: MuzeTooltipContext | any;
  allRawTicks?: any[];
  tickIndex?: number;
  dataPath?: MuzeFacetInfo[];
}

export interface MuzeTooltipContext {
  targetLayer: {
    config: () => any;
    metaInf: () => { totals?: string };
  };
}

export interface MuzeFacetInfo {
  value: any;
}

interface TextFormatData {
  field: FlamingoTypes.ModeField;
  format?: ChartTypes.TextFormat;
}

type DisplayNameMap = {
  [key: string]: string | undefined;
};

export function makeTabularData(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap
): MuzeChartSeries {
  const { marks } = encoding;
  const shared = marks.properties;
  const schema = new Array<MuzeDataField>();
  [
    ...encoding.column,
    ...encoding.x,
    ...encoding.x2,
    ...encoding.row,
    ...encoding.y,
    ...encoding.y2,
    ...shared.color.values,
    ...shared.size.values,
    ...shared.text.values,
    ...shared.detail.values,
    ...shared.tooltip.values,
    ...shared.angle.values,
  ].forEach((encField) => {
    const field = model.makeField(encField);
    if (field) {
      schema.push(buildMuzeDataField(model, encoding, field, fieldFormats));
    }
  });
  const dataset = model.getSeriesData('*');
  const hasMeasureNamesInSchema = (schema: MuzeDataSchema) => {
    return schema.some((field) => field.source === Attr.Source.Names);
  };

  if (hasMeasureNamesInData(dataset) && !hasMeasureNamesInSchema(schema)) {
    // Add a schema item for Measure Names so that it's always available
    // from Muze for tooltips and formatting.
    schema.push(makeMeasureNamesSchemaItem());
  }

  const { data, dataProperties } = formatTabularDataset(dataset, schema);
  const [oSchema, oData] = augmentWithNestedSorts(encoding, model, schema, data);

  const loadDataStart = performance.now();
  const muzeDataModel = loadMuzeData(oData, oSchema);
  dataProperties.loadTime = Math.round(performance.now() - loadDataStart);

  return {
    dataModel: muzeDataModel,
    schema,
    stats: {
      dataCount: data.length,
      schemaCount: schema.length,
      dataProperties,
    },
  };
}

export function augmentWithNestedSorts(
  encoding: Encoding,
  model: RenderDataModel,
  schema: MuzeDataSchema,
  data: MuzeDataset
): [MuzeDataSchema, MuzeDataset] {
  let oData = data;
  let oSchema = schema;

  const mergeFns: ((item: MuzeDataItem) => MuzeDataItem)[] = [];
  const nestedEncSorts = encoding.sorts.filter((s): s is EncSort.ByNested => {
    return s.type === SortType.NESTED;
  });

  for (const sort of nestedEncSorts) {
    const alias = sort.field;
    const byAlias = sort.byField.id as string;
    const nested = model.getNestedSortDomain(encoding, alias);
    if (nested !== undefined) {
      // Add the byField to the schema so muze can find/order by it
      oSchema = oSchema.concat({
        name: byAlias,
        source: Attr.Source.Field,
        type: 'measure',
        as: 'continuous',
      });

      // Add a function to merge this field's nested sort indices into the MuzeDataset
      const { keyFn, domain } = nested;
      mergeFns.push((item: MuzeDataItem) => {
        const key = keyFn(item);
        const value = item[alias];
        const indices = domain.get(key);

        let index = NaN;
        if (indices !== undefined) {
          index = indices.get(value) ?? NaN;
        }

        return {
          ...item,
          [byAlias]: index,
        };
      });
    }
  }

  if (mergeFns.length !== 0) {
    const addIndices = pipe(mergeFns);
    oData = oData.map((value) => {
      return addIndices(value);
    });
  }

  return [oSchema, oData];

  ///
  ///

  function pipe(fns: typeof mergeFns) {
    return fns.reduceRight((f1, f2) => {
      return <T extends MuzeDataItem>(value: T) => f1(f2(value));
    });
  }
}

export function makeVerticalData(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap
): MuzeChartSeries {
  const { seriesFields, chartData } = makeVerticalCombinedDataSet(model, encoding, format, fieldFormats);
  const { data, schema, dataProperties } = chartData;

  const [oSchema, oData] = augmentWithNestedSorts(encoding, model, schema, data);

  const loadDataStart = performance.now();
  const dataModel = loadMuzeData(oData, oSchema);
  const { data: cleanedDataModel, nNullsRemoved } = cleanDataModel(dataModel, model, [...encoding.y, ...encoding.y2]);

  dataProperties.loadTime = Math.round(performance.now() - loadDataStart);
  dataProperties.omittedNullCount = nNullsRemoved;

  return {
    dataModel: cleanedDataModel,
    schema,
    transform: makeTransforms(seriesFields),
    stats: {
      dataCount: data.length,
      schemaCount: schema.length,
      dataProperties,
    },
  };
}

export function makeVerticalCombinedDataSet(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap
): { seriesFields: EncField[]; chartData: MuzeChartData } {
  const sharedFields: EncField[] = [...encoding.column, ...encoding.row, ...encoding.y, ...encoding.y2];
  const xFields = [...encoding.x];
  const x2Fields = [...encoding.x2];
  return makeCombinedDataCommon(model, encoding, format, fieldFormats, sharedFields, xFields, x2Fields);
}

export function makeHorizontalData(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap
): MuzeChartSeries {
  const { seriesFields, chartData } = makeHorizontalCombinedDataSet(model, encoding, format, fieldFormats);
  const { data, schema, dataProperties } = chartData;

  const [oSchema, oData] = augmentWithNestedSorts(encoding, model, schema, data);

  const loadDataStart = performance.now();
  const dataModel = loadMuzeData(oData, oSchema);
  const { data: cleanedDataModel, nNullsRemoved } = cleanDataModel(dataModel, model, [...encoding.x, ...encoding.x2]);

  dataProperties.loadTime = Math.round(performance.now() - loadDataStart);
  dataProperties.omittedNullCount = nNullsRemoved;

  return {
    dataModel: cleanedDataModel,
    schema,
    transform: makeTransforms(seriesFields),
    stats: {
      dataCount: data.length,
      schemaCount: schema.length,
      dataProperties,
    },
  };
}

export function makeHorizontalCombinedDataSet(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap
): { seriesFields: EncField[]; chartData: MuzeChartData } {
  const sharedFields: EncField[] = [...encoding.column, ...encoding.x, ...encoding.x2, ...encoding.row];
  const yFields = [...encoding.y];
  const y2Fields = [...encoding.y2];
  return makeCombinedDataCommon(model, encoding, format, fieldFormats, sharedFields, yFields, y2Fields);
}

/**
 * Returns whether specified field is a converted continuous DATEPART.
 *
 * TODO: Replace this when Muze supports continuous DATEPARTs.
 *
 * @param {SchemaModel} model model
 * @param {Encoding} encoding encoding
 * @param {FlamingoTypes.ModeField} field field
 * @returns true iff specified field is a converted continuous DATEPART
 */
export function treatContinuousDatePartAsDiscrete(
  model: SchemaModel,
  encoding: Encoding,
  field?: FlamingoTypes.ModeField
): boolean {
  if (
    field == null ||
    field.source === Attr.Source.Names ||
    field.source === Attr.Source.Values ||
    field.type === Attr.Type.Discrete
  ) {
    return false;
  }
  const encField = EncodingHelpers.getEncFieldById(encoding, field.alias);
  if (encField && encField.groupId != null) {
    // We never want to treat a shared axis field as discrete.
    return false;
  }

  const compiled = model.compileField(field);
  if (!compiled || !compiled.isValid) {
    return false;
  }
  const axisFields: EncField[] = [...encoding.x, ...encoding.x2, ...encoding.y, ...encoding.y2];
  if (!axisFields.find((element) => element.id === field.alias)) {
    return false;
  }
  return (
    compiled.hasDatePart &&
    compiled.formulaDataType !== DataType.Name.DateTime &&
    compiled.formulaDataType !== DataType.Name.Timestamp
  );
}

export function isContinuousField(model: RenderDataModel, encField: EncField) {
  if (!encField.id) {
    return false;
  }
  const compiledField = model.compileEncField(encField);
  if (!compiledField.isValid) {
    return false;
  }
  return compiledField.isContinuous && !compiledField.isAggregateStage;
}

export function getMuze(): Muze {
  return muze as unknown as Muze;
}

export function loadMuzeData(data: MuzeDataset, schema: MuzeDataSchema): MuzeDataModel {
  const DataModel = muze.DataModel;
  DataModel.defaultInvalidValue(FieldFormatHelpers.MISSING_VALUE);

  const dm = new DataModel(DataModel.loadDataSync(data, schema), { enableDomainCompletion: false });
  return dm;
}

/**
 * Helper function to return the first id in the specified array that identifies a field
 * in the specified channel matching the specified field's formula and variable type.
 *
 * @param {readonly Readonly<EncSeries>[]} allSeries all series
 * @param {MarkChannel} channel mark channel for the series
 * @param {string[]} multiple Array of field ids
 * @param {string|undefined} id id of field to be matched, or undefined for no match
 * @return {string|undefined} first matching id, or undefined if no match found
 */
export function getMultipleRefId(
  allSeries: readonly Readonly<EncSeries>[],
  channel: MarkChannel,
  multiple: string[],
  id: string | undefined
): string | undefined {
  let field: EncField | undefined;

  // Find the specified field.
  for (let i = 0; i < allSeries.length && !field; i++) {
    field = allSeries[i].properties[channel].values.find((f) => f.id === id);
  }

  // Return the first id of a field in the specified channel that matches the specified field's formula and variable type.
  return multiple.find((id: string) =>
    allSeries.some((series) =>
      series.properties[channel].values.some(
        (f) => f.id === id && field && f.formula === field.formula && f.variable.type === field.variable.type
      )
    )
  );
}

/**
 * Helper function to get a series' mark channel fields. Returns both the unique and summary fields
 * for all mark channels as well as a list of fields that reference a shared summary field.
 *
 * @param {readonly Readonly<EncSeries>[]} allSeries all series
 * @param {number} index index of series to operate over
 * @param {MarkChannel} channel mark channel for the given series
 * @param {MarksSummary} summary of the series encodings to compare against
 */
export function buildChannelSeriesFields(
  allSeries: readonly Readonly<EncSeries>[],
  index: number,
  channel: MarkChannel,
  summary: MarksSummary
): [EncField[], MarkSeriesRef[]] {
  const series: EncSeries = allSeries[index];
  const summaryChannel = (summary as any)[`${channel}Ids`];
  const multiple: string[] = summaryChannel?.multiple ?? [];

  // A valid channel is either the main field in the summary channel or
  // is the first instance of this field in the list of multiple channel ids.
  const channelValues = series.properties[channel].values.filter((field) => {
    return (
      summary[channel].some((summaryField) => summaryField.id === field.id) ||
      getMultipleRefId(allSeries, channel, multiple, field.id) === field.id
    );
  });

  // For all other fields, store an array of the field id and the shared summary field as a reference.
  // This will be used to build a combined dataset including the field data in the reference summary field column for the given series.
  const hiddenChannel = series.properties[channel].values.filter(
    (field) => !channelValues.some((channelField) => channelField.id === field.id)
  );
  const hiddenChannelWithRef: MarkSeriesRef[] = hiddenChannel.map((field) => {
    const referenceId = Object.keys(summaryChannel).find(
      (id) => id !== 'multiple' && summaryChannel[id].includes(field.id)
    );
    return {
      id: field.id,
      referenceId: referenceId ? referenceId : getMultipleRefId(allSeries, channel, multiple, field.id),
    };
  });

  return [channelValues, hiddenChannelWithRef];
}

/**
 * Helper function to get all of a series' marks channel fields. Returns both the unique and summary fields
 * for all mark channels as well as a list of fields that reference a shared summary field.
 *
 * @param {readonly Readonly<EncSeries>[]} allSeries all series
 * @param {MarksSummary} summary of the series encodings to compare against
 * @param {number} index index of series to operate over
 */
export function getSeriesMarksSummaryAt(
  allSeries: readonly Readonly<EncSeries>[],
  summary: MarksSummary,
  index: number
): SeriesMarkSummary {
  const series = allSeries[index];
  if (series == null) {
    throw new Error(`Series index is out of range. Series size is ${allSeries.length}, saw ${index}`);
  }

  const [color, colorRefs] = buildChannelSeriesFields(allSeries, index, 'color', summary);
  const [size, sizeRefs] = buildChannelSeriesFields(allSeries, index, 'size', summary);
  const [text, textRefs] = buildChannelSeriesFields(allSeries, index, 'text', summary);
  const [detail, detailRefs] = buildChannelSeriesFields(allSeries, index, 'detail', summary);
  const [tooltip, tooltipRefs] = buildChannelSeriesFields(allSeries, index, 'tooltip', summary);
  const [angle, angleRefs] = buildChannelSeriesFields(allSeries, index, 'angle', summary);
  const [baseline, baselineRefs] = buildChannelSeriesFields(allSeries, index, 'baseline', summary);
  return {
    series: series.field,
    unique: [...color, ...size, ...text, ...detail, ...tooltip, ...angle, ...baseline],
    shared: [...colorRefs, ...sizeRefs, ...textRefs, ...detailRefs, ...tooltipRefs, ...angleRefs, ...baselineRefs],
  };
}

export function buildMuzeTextFormatter(
  model: SchemaModel,
  fields: FlamingoTypes.ModeField[],
  fieldFormats: ChartTypes.FieldFormatMap,
  measureNamesId?: string,
  parentField?: FlamingoTypes.ModeField,
  displayNameMap?: DisplayNameMap
) {
  const makeFormatData = (modeField: FlamingoTypes.ModeField) => {
    const paneFormat = FieldFormatHelpers.resolvePaneTextFormatForField(fieldFormats, modeField);
    const resolvedFormat = FieldFormatHelpers.resolveTextFormatForCompiledField(
      model.compileField(modeField),
      paneFormat
    );
    return {
      field: modeField,
      format: resolvedFormat,
    };
  };

  const formatData: TextFormatData[] = fields.map((field) => makeFormatData(field));
  const parentFormatData = parentField != null ? makeFormatData(parentField) : undefined;
  const subtotalFormatterFn = makeSubtotalFormatter(model, parentFormatData, displayNameMap);

  return buildMuzeTextFormatterFromFormat(model, formatData, measureNamesId, subtotalFormatterFn);
}

export function buildMeasureNamesFieldMap(
  model: RenderDataModel,
  fields: Readonly<EncField[]>,
  fieldFormatMap: ChartTypes.FieldFormatMap
) {
  const fieldMap: DisplayNameMap = {};

  fields
    .map((field) => model.makeField(field))
    .forEach((field) => {
      // Non-system continuous measure
      if (
        field?.role === Attr.Role.Measure &&
        field.type === Attr.Type.Continuous &&
        field.source !== Attr.Source.Values
      ) {
        const formatKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
        fieldMap[field.name] =
          fieldFormatMap[formatKey]?.axisFormat?.title?.text ||
          FieldFormatHelpers.getBaseFormatDisplayName(formatKey, fieldFormatMap);
      }
    });

  return fieldMap;
}

export function getMuzeTextFormatter(
  model: RenderDataModel,
  encoding: Encoding,
  field: FlamingoTypes.ModeField | undefined,
  fieldFormatMap: ChartTypes.FieldFormatMap,
  parentField?: FlamingoTypes.ModeField
) {
  if (!field) {
    return;
  }
  const fields = [encoding.x, encoding.x2, encoding.y, encoding.y2, encoding.value].flat();

  if (field.source === Attr.Source.Names) {
    return buildMuzeMeasureNamesTextFormatter(buildMeasureNamesFieldMap(model, fields, fieldFormatMap));
  }

  const displayNameMap =
    parentField?.source === Attr.Source.Names ? buildMeasureNamesFieldMap(model, fields, fieldFormatMap) : undefined;

  return buildMuzeTextFormatter(model, [field], fieldFormatMap, undefined, parentField, displayNameMap);
}

export function buildMuzeMeasureNamesTextFormatter(fieldMap: { [key: string]: string | undefined }): MuzeDataFormatFn {
  return (dataInfo: MuzeTextDataInfo) => {
    const value = dataInfo.rawValue;
    return fieldMap[value] || value;
  };
}

export function buildMuzeTextFormatterFromFormat(
  model: SchemaModel,
  formatData: TextFormatData[],
  measureNamesId?: string,
  subtotalFormatterFn?: SubtotalFormatFn
): MuzeDataFormatFn | undefined {
  if (formatData.length === 0) {
    return;
  }
  let fieldMap: Map<FlamingoTypes.ModeField, CompiledField> = new Map();

  return function textFormatter(dataInfo: MuzeTextDataInfo, contextInfo: MuzeContextInfo) {
    const value = dataInfo.rawValue;
    // Muze provides one of two functions to get the row data depending on whether or not the
    // chart is configured with `showStackSum` = true. When true, the text is placed on top of
    // the bar and is the sum of all of the segments. When false, the text is not summed and is
    // shown in each segment.
    // We can no longer just call `getRowData` to get the data. We first have to check
    // the configuration for `showStackSum` and then either use `getRowData` or `getStackSumData`.
    // This code relies on our Muze configuration only setting `showStackSum` in cases where it
    // is valid. Calling the wrong function will throw an error.
    const showStackSum = contextInfo?.context?._config?.encoding?.text?.showStackSum ?? false;
    let rowData = {};
    try {
      if (showStackSum && typeof dataInfo.getStackSumData === 'function') {
        rowData = dataInfo.getStackSumData();
      } else if (typeof dataInfo.getRowData === 'function') {
        rowData = dataInfo.getRowData();
      }
      // eslint-disable-next-line no-empty
    } catch (error) {}
    const { format, field } = resolveFieldFormat(formatData, rowData, measureNamesId);
    if (!field) {
      return value;
    }

    if (!fieldMap.has(field)) {
      fieldMap.set(field, model.compileField(field));
    }

    const compiled = fieldMap.get(field);
    if (!compiled) {
      return value;
    }

    if (dataInfo.formattedValue === SUBTOTAL_TOKEN) {
      // Special handling for subtotals
      return subtotalFormatterFn?.(contextInfo) ?? 'Subtotal';
    }

    if (!compiled.isBinned) {
      return FieldFormatHelpers.formatValue(value, compiled, format);
    }

    if (compiled.isBinned) {
      // Not sure why but for a binned field, the formatted value for a subtotal is the TOTALS_TOKEN
      // and not the SUBTOTAL_TOKEN
      if (dataInfo.formattedValue === TOTALS_TOKEN) {
        return subtotalFormatterFn?.(contextInfo) ?? 'Subtotal';
      }
      // For axis, we only want the low value of the bin to be formatted and displayed
      const isBinnedAxis = contextInfo.context?.axis && contextInfo.context?.isBinnedAxis?.();
      const isWholeRange = contextInfo?.context?._config?.bins?.display === 'wholeRange';

      // Muze sends the value as a tuple of [low, high] for axes. Just taking the low value here.
      if (isBinnedAxis && isWholeRange) {
        if (muze.DataStore.isInvalid(value)) {
          return FieldFormatHelpers.formatValue(value.value(), compiled, format);
        }

        const formattedLow = FieldFormatHelpers.formatValue(value[0], compiled, format);
        const formattedHigh = FieldFormatHelpers.formatValue(value[1], compiled, format);
        return `${formattedLow}, ${formattedHigh}`;
      }

      if (isBinnedAxis) {
        const formattedValue = FieldFormatHelpers.formatValue(
          muze.DataStore.isInvalid(value) ? value.value() : value[0],
          compiled,
          format
        );
        return formattedValue;
      }

      // For other cases, we want the range of the bin to be formatted and displayed
      // For step legend tooltip, Muze sends range
      if (Array.isArray(value)) {
        const formattedLow = FieldFormatHelpers.formatValue(value[0], compiled, format);
        const formattedHigh = FieldFormatHelpers.formatValue(value[1], compiled, format);
        return `${formattedLow}, ${formattedHigh}`;
      }

      // Muze sends just the low values.
      const formattedLow = FieldFormatHelpers.formatValue(
        muze.DataStore.isInvalid(value) ? value.value() : value,
        compiled,
        format
      );

      if (contextInfo.context?.payload?.action === 'highlight' && !muze.DataStore.isInvalid(value)) {
        // For highlight interactions (tooltips), we add the low values with the bin size to get
        // the high value.
        const formattedHigh = FieldFormatHelpers.formatValue(value + compiled.binSize, compiled, format);

        return `${formattedLow}, ${formattedHigh}`;
      } else {
        return formattedLow;
      }
    }
  };
}

export function makeSubtotalFormatter(
  model: SchemaModel,
  parentFormatData?: TextFormatData,
  displayNameMap?: DisplayNameMap
) {
  return function formatSubtotal(contextInfo: MuzeContextInfo) {
    // Handling subtotal formatting for Muze is complicated.
    // Subtotals need to be formatted as '{Parent facet} total'.
    // For example, the label should be "East total".
    // Region  |  State     |  SUM(Price)
    // ----------------------------------
    // East    |    NY      |    10
    //         |    NJ      |    15
    //         |    MA      |    10
    //         | East total |    35
    //
    //
    // Therefore, to format the subtotal from "State", we need the current value of "Region"
    // Also, the parent value might need to be formatted as well. For example, if the parent
    // is MONTH(date) we want to format a raw value of "1" to "January".
    //
    // The axes and facets pass different information during the formatting. We however use
    // a single formatter so we have to dig out the right information. Luckily, the location
    // of the data is mutually exclusive so once we find it, we know we have the right stuff.

    const getParentFacetData = () => {
      if (contextInfo.context?.config()?.metaInf?.facets != null) {
        const facets = contextInfo.context.config().metaInf.facets as MuzeFacetInfo[];
        return facets[facets.length - 1];
      }
      if (contextInfo.dataPath != null) {
        const facets = contextInfo.dataPath;
        return facets[facets.length - 2];
      }
    };

    const parentFacet = getParentFacetData();
    const parentFacetValue = parentFacet?.value ?? '';

    if (parentFacetValue === SUBTOTAL_TOKEN) {
      // When the parent is also a subtotal, we display an empty cell.
      return '';
    }

    if (parentFormatData == null) {
      return `${parentFacetValue} total`;
    }

    if (parentFormatData.field.source === Attr.Source.Names) {
      const measureDisplayName = displayNameMap?.[parentFacetValue] ?? parentFacetValue;
      return `${measureDisplayName} total`;
    }

    // Format the parent value
    const formattedParent = FieldFormatHelpers.formatValue(
      parentFacetValue,
      model.compileField(parentFormatData.field),
      parentFormatData.format
    );
    return `${formattedParent} total`;
  };
}

export function getAxisScaleRange(format?: ChartTypes.FieldFormat) {
  if (format) {
    return format.axisFormat?.scale?.range;
  }
}

export function getLegendVisibility(format?: ChartTypes.FieldFormat): boolean {
  return format?.[ChartTypes.FieldFormatTypes.Legend]?.show ?? true;
}

export function getAxisTitleFormat(format?: ChartTypes.FieldFormat): ChartTypes.TitleFormat | undefined {
  return getTitleFormatForType(format, ChartTypes.FieldFormatTypes.Axis);
}

export function getHeaderTitleFormat(format?: ChartTypes.FieldFormat): ChartTypes.TitleFormat | undefined {
  return getTitleFormatForType(format, ChartTypes.FieldFormatTypes.Header);
}

export function getLegendTitle(format?: ChartTypes.FieldFormat): { text: string; show: boolean } {
  const legendFormat = format?.[ChartTypes.FieldFormatTypes.Legend];
  const baseFormatDisplayName = format?.[ChartTypes.FieldFormatTypes.Base]?.[ChartTypes.FieldBaseTypes.DisplayName];
  const showTitle = legendFormat?.showTitle ?? true;
  // If we are not displaying a title, use a single space as the text. This reduces the overall
  // size of the legend. If we pass null or an empty string, Muze appears to measure the default
  // text (which is the field name) when determining the overall width of the legend even though
  // the text isn't displayed.
  const text = showTitle ? (legendFormat?.[ChartTypes.FieldLegendTypes.Title] || baseFormatDisplayName) ?? '' : ' ';
  return { text, show: showTitle };
}

export function getTitleFormatForType(
  format: ChartTypes.FieldFormat | void,
  formatType: ChartTypes.FieldFormatTypes
): ChartTypes.TitleFormat | undefined {
  if (format) {
    switch (formatType) {
      case ChartTypes.FieldFormatTypes.Axis: {
        return format[ChartTypes.FieldFormatTypes.Axis]?.[ChartTypes.FieldAxisTypes.Title];
      }
      case ChartTypes.FieldFormatTypes.Header: {
        return format[ChartTypes.FieldFormatTypes.Header]?.[ChartTypes.FieldHeaderTypes.Title];
      }
      // falls through
      case ChartTypes.FieldFormatTypes.Pane:
      default: {
        // Same as without a format
        return undefined;
      }
    }
  }
}

export function getAxisLabelFormat(format?: ChartTypes.FieldFormat): ChartTypes.LabelFormat | undefined {
  return getLabelFormatForType(format, ChartTypes.FieldFormatTypes.Axis);
}

export function getHeaderLabelFormat(format?: ChartTypes.FieldFormat): ChartTypes.LabelFormat | undefined {
  return getLabelFormatForType(format, ChartTypes.FieldFormatTypes.Header);
}

function getLabelFormatForType(
  format: ChartTypes.FieldFormat | void,
  formatType: ChartTypes.FieldFormatTypes
): ChartTypes.LabelFormat | undefined {
  if (format) {
    switch (formatType) {
      case ChartTypes.FieldFormatTypes.Axis: {
        return format.axisFormat?.label;
      }
      case ChartTypes.FieldFormatTypes.Header: {
        return format.headerFormat?.label;
      }
      // falls through
      case ChartTypes.FieldFormatTypes.Pane:
      default: {
        // Same as without a format
        return undefined;
      }
    }
  }
}

export function getAxisInterpolatorType(format?: ChartTypes.FieldFormat): ChartTypes.AxisScaleTypes {
  if (format?.axisFormat?.scale?.interpolation == null) {
    return ChartTypes.AxisScaleTypes.Linear;
  }
  return format.axisFormat.scale?.interpolation.type;
}

export function buildMuzeDataField(
  model: SchemaModel,
  encoding: Encoding,
  field: FlamingoTypes.ModeField,
  fieldFormats: ChartTypes.FieldFormatMap
): MuzeDataField {
  const compiled = model.compileField(field);
  const fieldFormatKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
  const headerTitle =
    fieldFormats[fieldFormatKey]?.headerFormat?.title?.text ||
    fieldFormats[fieldFormatKey]?.baseFormat?.displayName ||
    compiled.displayFormula;
  const axisTitle = resolveAxisTitle(compiled, model, encoding, fieldFormats);
  const isBinned = compiled.isBinned;

  switch (field.source) {
    case Attr.Source.Names: {
      return {
        name: field.alias,
        type: 'dimension',
        source: field.source,
        as: 'discrete',
        subtype: 'categorical',
        displayName: headerTitle,
      };
    }
    case Attr.Source.Values: {
      return {
        name: field.alias,
        type: 'measure',
        source: field.source,
        as: 'continuous',
        displayName: axisTitle,
      };
    }
    default: {
      switch (field.type) {
        case Attr.Type.Discrete: {
          switch (compiled.formulaDataType) {
            case DataType.Name.Numeric:
            case DataType.Name.Integer:
            case DataType.Name.Float:
            case DataType.Name.Decimal: {
              return {
                name: field.alias,
                type: 'dimension',
                source: field.source,
                subtype: isBinned ? 'binned' : undefined,
                as: isBinned ? undefined : 'discrete',
                binSize: isBinned ? compiled.binSize : undefined,
                displayName: headerTitle,
              };
            }
            default: {
              return {
                name: field.alias,
                type: 'dimension',
                source: field.source,
                as: 'discrete',
                subtype: 'categorical',
                displayName: headerTitle,
              };
            }
          }
        }

        case Attr.Type.Continuous: {
          const isPartOfDualAxis = EncodingHelpers.isOneOfDualAxisPair(encoding, field.alias);
          const isPartOfSharedAxis = SharedAxisHelpers.isSharedAxisMember(encoding, field.alias);
          const isTemporal = compiled.isFormulaDate && !isPartOfDualAxis && !isPartOfSharedAxis;

          return {
            name: field.alias,
            type: field.role === Attr.Role.Dimension ? 'dimension' : 'measure',
            source: field.source,
            as: isBinned ? undefined : 'continuous',
            subtype: isBinned ? 'binned' : isTemporal ? 'temporal' : 'continuous',
            binSize: isBinned ? compiled.binSize : undefined,
            displayName: axisTitle,
          };
        }
      }
    }
  }
}

/**
 * @description
 * Determines the axis title based on either the FieldFormat or the
 * CompiledField's display formula. Handles special fallback options
 * for Measure Values.
 */
function resolveAxisTitle(
  field: CompiledField,
  model: SchemaModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap
) {
  const fieldFormatKey = FieldFormatHelpers.getFormatMapKey(
    field.formula,
    field.isDiscrete ? VariableType.DISCRETE : VariableType.CONTINUOUS
  );
  const titleFormat = getAxisTitleFormat(fieldFormats[fieldFormatKey]);
  const displayName = FieldFormatHelpers.getBaseFormatDisplayName(fieldFormatKey, fieldFormats);
  const titleText = (titleFormat?.text || displayName) ?? '';
  // Special case:
  //   this is Measure Values AND
  //   there is one field in value AND
  //   there is no title set on Measure Values,
  // THEN use the title based the singular value field.
  if (field.isValues && encoding.value.length === 1 && titleText === '') {
    const valueField = encoding.value[0];
    const valueFormatKey = FieldFormatHelpers.getFormatMapKeyByField(valueField);
    const valueTitleFormat = getAxisTitleFormat(fieldFormats[valueFormatKey]);
    const displayName = FieldFormatHelpers.getBaseFormatDisplayName(valueFormatKey, fieldFormats);
    const valueTitleText = (valueTitleFormat?.text || displayName) ?? '';
    return valueTitleText || model.compileEncField(valueField).displayFormula;
  } else {
    return titleText || field.displayFormula;
  }
}

/**
 * Determines the appropriate TextFormatData for this data row. If there is only
 * one TextFormatData passed in the formatData, then that is returned. If the
 * array of formatData contains Measure Values plus the associated values, then
 * the format for Measure Values should always be the first element in the array.
 * @param formatData Array of field and format information
 * @param rowData Object containing all of the data for the row to format or an empty object
 * @param measureNamesId string id of Measure Names field if there is one
 * @returns TextFormatData or undefined to use to format this data
 */
function resolveFieldFormat(formatData: TextFormatData[], rowData: any, measureNamesId?: string) {
  let format = formatData[0];
  if (formatData.length > 1) {
    // If there is more than one, this is the Measure Values case. The format for Measure Values
    // should be the first element. Attempt to use the value in Measure Names to find a more
    // specific format. We always include Measure Names in the data passed to Muze when Measure
    // Values is present, even if Measure Names isn't in the encoding. When Measure Names isn't
    // in the encoding, the field name is Names.Key.
    const measureName = rowData[measureNamesId ?? Names.Key];
    if (measureName != null) {
      const fieldAndFormat = formatData.find(({ field }) => {
        return measureName === field.name;
      });
      if (fieldAndFormat && fieldAndFormat.format) {
        format = fieldAndFormat;
      }
      // If this format is Auto, use the Measure Values format
      if (
        FieldFormatHelpers.isAutomaticShortcutType(format.format?.shortcutType) &&
        formatData[0].field.formula.source === Values.Key
      ) {
        format = formatData[0];
      }
    }
  }
  return format;
}

function makeCombinedDataCommon(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldFormats: ChartTypes.FieldFormatMap,
  sharedFields: EncField[],
  primaryFields: Readonly<EncField>[],
  secondaryFields: Readonly<EncField>[]
): { seriesFields: EncField[]; chartData: MuzeChartData } {
  const { marks } = encoding;
  const seriesFields: EncField[] = [];
  const uniqueFields: Array<EncField[]> = [];
  const datasets: Array<QueryResult.Item[]> = [];
  const summary: MarksSummary = EncodingHelpers.getSummary(encoding);
  const sharedMarkFields: Array<MarkSeriesRef[]> = [];

  marks.series.forEach((series, index, allSeries) => {
    const primaryField = primaryFields.find((field) => field.id == series.field);
    const secondaryField = secondaryFields.find((field) => field.id == series.field);

    if (primaryField != null) {
      seriesFields.push(primaryField);
    } else if (secondaryField != null) {
      seriesFields.push(secondaryField);
    } else {
      throw new Error(`Series not found ${series.field}`);
    }

    // list of all fields in the mark channels per series that share
    // a common summary field and a reference to the summary field
    const { shared, unique } = getSeriesMarksSummaryAt(allSeries, summary, index);
    sharedMarkFields.push(shared);
    uniqueFields.push(unique);

    datasets.push(model.getSeriesData(series.field) as QueryResult.Item[]);
  });
  const chartData = makeCombinedDataset(
    model,
    encoding,
    seriesFields,
    sharedFields,
    uniqueFields,
    sharedMarkFields,
    datasets,
    fieldFormats
  );

  // Convert continuous DATEPARTs on axes to discrete.
  // TODO: Replace this when Muze supports continuous DATEPARTs.
  const { schema } = chartData;
  const axisFields = [...encoding.x, ...encoding.x2, ...encoding.y, ...encoding.y2];
  axisFields.forEach((encField) => {
    const modeField = model.makeField(encField);
    if (modeField && encField.groupId == null) {
      const compiled = model.compileField(modeField);
      if (compiled.hasDatePart && compiled.isContinuous) {
        const muzeFieldIndex = schema.findIndex((f) => f.name === encField.id);
        if (muzeFieldIndex >= 0) {
          (schema as Array<MuzeDataField>)[muzeFieldIndex] = {
            ...schema[muzeFieldIndex],
            as: 'discrete',
            subtype: 'categorical',
          };
        }
      }
    }
  });

  return { seriesFields, chartData };
}

/**
 *
 * Creates a shared dataset that can be loaded into Muze
 *
 * This method works by combining the dataset columns into a single schema
 *  and appending the datasets together. A unique system field named $SERIESID
 *  is populated using the alias of each series field. The $SERIESID field can
 *  be used in muze transformation to extract the individual datasets out of the
 *  combined dataset during rendering.
 *
 * @param datasets - The datasets returned by the underlying model
 * @param seriesFields - The set of series fields associated with each dataset
 * @param sharedFields - The set of dimensions that are shared among all series
 * @param uniqueFields - The set of unique fields that are present within each series
 *
 */

function makeCombinedDataset(
  model: SchemaModel,
  encoding: Encoding,
  seriesFields: Array<EncField>,
  sharedFields: Array<EncField>,
  uniqueFields: Array<EncField[]>,
  sharedMarkFields: Array<any>,
  datasets: Array<QueryResult.Item[]>,
  fieldFormats: ChartTypes.FieldFormatMap
): MuzeChartData {
  const data: MuzeDataItem[] = [];
  const templateItem: MuzeDataItem = {};
  const schema = new Array<MuzeDataField>();
  const discreteUniqueMap: Record<string, Record<string, number>> = {};

  schema.push({
    name: SID_FIELD,
    type: 'dimension',
    source: Attr.Source.Field,
    subtype: 'categorical',
    displayName: 'Series ID',
    as: 'discrete',
  });

  templateItem[SID_FIELD] = null;

  let hasNames = false;

  const addFieldToSchema = (modeField: FlamingoTypes.ModeField | undefined) => {
    if (modeField) {
      schema.push(buildMuzeDataField(model, encoding, modeField, fieldFormats));
      hasNames ||= modeField.source === Attr.Source.Names;
    }
  };

  for (const sharedField of sharedFields) {
    if (!sharedField.id) {
      continue;
    }
    templateItem[sharedField.id] = null;
    addFieldToSchema(model.makeField(sharedField));
  }

  seriesFields.forEach((seriesField, index) => {
    if (!seriesField.id) {
      return;
    }
    templateItem[seriesField.id] = null;
    addFieldToSchema(model.makeField(seriesField));

    for (const uniqueField of uniqueFields[index]) {
      if (!uniqueField.id) {
        continue;
      }
      templateItem[uniqueField.id] = null;
      addFieldToSchema(model.makeField(uniqueField));
    }
  });

  const needsNames = !hasNames && hasMeasureNamesInData(datasets[0]);
  if (needsNames) {
    schema.push(makeMeasureNamesSchemaItem());
  }

  seriesFields.forEach((field, index) => {
    const dataset = datasets[index];
    const seriesFields = uniqueFields[index];
    const markFields = sharedMarkFields[index];

    for (const record of dataset) {
      const item = { ...templateItem };

      item[SID_FIELD] = field.id;
      if (!field.id) {
        continue;
      }
      item[field.id] = record[field.id];

      if (needsNames) {
        item[Names.Key] = record[Names.Key];
      }

      for (const sharedField of sharedFields) {
        if (!sharedField.id) {
          continue;
        }
        item[sharedField.id] = getFieldFromRecord(sharedField, record);
        if (sharedField.variable?.type === VariableType.DISCRETE) {
          discreteUniqueMap[sharedField.id] = discreteUniqueMap[sharedField.id] ?? {};
          discreteUniqueMap[sharedField.id][item[sharedField.id]] = 1;
        }
      }

      for (const seriesField of seriesFields) {
        if (!seriesField.id) {
          continue;
        }
        item[seriesField.id] = getFieldFromRecord(seriesField, record);
        if (seriesField.variable?.type === VariableType.DISCRETE) {
          discreteUniqueMap[seriesField.id] = discreteUniqueMap[seriesField.id] ?? {};
          discreteUniqueMap[seriesField.id][item[seriesField.id]] = 1;
        }
      }

      for (const markField of markFields) {
        item[markField.referenceId] = getFieldFromRecord(markField, record);
        if (markField.variable?.type === VariableType.DISCRETE) {
          discreteUniqueMap[markField.referenceId] = discreteUniqueMap[markField.referenceId] ?? {};
          discreteUniqueMap[markField.referenceId][item[markField.referenceId]] = 1;
        }
      }

      data.push(item);
    }
  });

  const discreteCount: Record<string, number> = {};
  for (const [key, value] of Object.entries(discreteUniqueMap)) {
    discreteCount[key] = Object.keys(value).length;
  }

  const dataProperties: MuzeDataProperties = {
    discreteCount,
    dataLength: data.length,
  };

  return { data, schema, dataProperties };
}

function cleanDataModel(dataModel: MuzeDataModel, model: RenderDataModel, encodingFields: EncField[]) {
  const DataModel = muze.DataModel;
  const originalSize = dataModel.getDataMeta().rows;
  const conditions: MuzeFilterCondition[] = [];
  encodingFields.forEach((encField) => {
    if (isContinuousField(model, encField) && encField.id) {
      conditions.push({
        field: encField.id,
        value: DataModel.Invalid.NULL,
        operator: DataModel.ComparisonOperators.NOT_EQUAL,
      });
    }
  });
  if (conditions.length > 0) {
    // NOTE: This creates a new data model which is a child of the passed data model. We can not
    // dispose of the parent model since that will dispose of this child too. We will dispose
    // of both when the viz is destroyed.
    dataModel = dataModel.select({ conditions: conditions, operator: DataModel.LogicalOperators.AND });
  }
  const nFiltered = originalSize - dataModel.getDataMeta().rows;
  return { data: dataModel, nNullsRemoved: nFiltered };
}

function hasMeasureNamesInData(data: readonly QueryResult.Item[]) {
  return data.length > 0 && Object.prototype.hasOwnProperty.call(data[0], Names.Key);
}

function makeMeasureNamesSchemaItem(): MuzeDataField {
  return {
    name: Names.Key,
    type: 'dimension',
    source: Attr.Source.Names,
    as: 'discrete',
    subtype: 'categorical',
    displayName: Names.Name,
  };
}

function formatTabularDataset(
  dataset: MuzeDataset,
  schema: MuzeDataSchema
): { data: MuzeDataset; dataProperties: MuzeDataProperties } {
  const data: MuzeDataItem[] = [];
  const discreteUniqueMap: Record<string, Record<string, number>> = {};

  for (const record of dataset) {
    const item: Record<string, any> = {};

    for (const field of schema) {
      if (record[field.name] === null) {
        item[field.name] = null;
      } else {
        item[field.name] = record[field.name];
      }

      if (field.as === 'discrete') {
        discreteUniqueMap[field.name] = discreteUniqueMap[field.name] ?? {};
        discreteUniqueMap[field.name][item[field.name]] = 1;
      }
    }

    data.push(item);
  }

  const discreteCount: Record<string, number> = {};
  for (const [key, value] of Object.entries(discreteUniqueMap)) {
    discreteCount[key] = Object.keys(value).length;
  }
  const dataProperties: MuzeDataProperties = {
    discreteCount,
    dataLength: data.length,
    omittedNullCount: 0,
  };

  return { data, dataProperties };
}

export function calculateFacetCounts(dataModel: MuzeDataModel, dataRowCount: number, encoding: Encoding) {
  const facetCount = (fields: Readonly<EncField[]>) => {
    const groupBy = fields.map((encField) => encField.id);
    if (groupBy.length === 0) {
      return 0;
    }
    // Do a groupBy on the Muze data set using only the fields on the channel.
    // This aggregates to a higher level and the row count will be the facet count.
    const groupedData = dataModel.groupBy(groupBy, []);
    const count = groupedData.getData().data.length;
    // Dispose of the extra data model we just created. This data model is a child to the
    // original data model, so technically, it will be disposed when that one is. However,
    // that probably is when the viz is destroyed so it's worth getting rid of it proactively.
    groupedData.dispose();
    return count;
  };

  const rowFacetCount = facetCount(encoding.row) || 1;
  const columnFacetCount = facetCount(encoding.column) || 1;

  const rows = encoding.row.concat(encoding.marks.channel !== 'y' ? encoding.y : []);
  const discreteRows = rows.filter((encField) => encField.variable.type === VariableType.DISCRETE);
  // Avoid doing the work of calculating the facet count if the fields are the same
  const rowCount = rows.length === encoding.row.length ? rowFacetCount : facetCount(rows) || 1;

  const columns = encoding.column.concat(encoding.marks.channel !== 'x' ? encoding.x : []);
  const discreteColumns = columns.filter((encField) => encField.variable.type === VariableType.DISCRETE);
  // Avoid doing the work of calculating the facet count if the fields are the same
  const columnCount = columns.length === encoding.column.length ? columnFacetCount : facetCount(columns) || 1;
  const measureCount =
    encoding.marks.channel === 'x'
      ? encoding.x.length || 1
      : encoding.marks.channel === 'y'
      ? encoding.y.length || 1
      : 1;
  // elementCount roughly calculates how many data elements plus facet labels are in the
  // visualization. For example, in a table this would include all of the value cells, plus
  // the labels on the rows and on the columns.
  const elementCount =
    Math.min(rowCount * columnCount * measureCount, dataRowCount) +
    rowCount * discreteRows.length +
    columnCount * discreteColumns.length;

  return { rowCount, columnCount, rowFacetCount, columnFacetCount, measureCount, elementCount };
}

export function calculateDataSizes(
  dataModel: MuzeDataModel,
  encoding: Encoding,
  dataCount: number,
  discreteCounts: { [seriesId: string]: number }
) {
  const dataStats: VisualizationDataStats = {};

  const { rowCount, columnCount, rowFacetCount, columnFacetCount, measureCount, elementCount } = calculateFacetCounts(
    dataModel,
    dataCount,
    encoding
  );
  const totalFacets = rowFacetCount * columnFacetCount * measureCount;

  dataStats.rowCount = rowCount;
  dataStats.columnCount = columnCount;
  dataStats.facetCount = totalFacets;
  dataStats.rowFacets = rowFacetCount;
  dataStats.colFacets = columnFacetCount;
  dataStats.measureCount = measureCount;
  dataStats.elementCount = elementCount;

  // Check the series count for non-tabular encodings. Tables will be handled by table-specific checks.
  const layerCounts: Record<MarkChannel, { id: string; value: number } | undefined>[] = [];
  if (encoding.marks.channel !== '*') {
    const summary = EncodingHelpers.getSummary(encoding);
    const getSeriesChannelCount = (all: Readonly<EncSeries[]>, index: number, channel: MarkChannel) => {
      const [field, refs] = buildChannelSeriesFields(all, index, channel, summary);
      if (field[0]?.id != null) {
        return { id: field[0].id, value: discreteCounts[field[0].id] };
      } else if (refs.length > 0) {
        return { id: refs[0].referenceId ?? '', value: discreteCounts[refs[0].referenceId ?? ''] };
      }
    };
    encoding.marks.series.forEach((series, index, allSeries) => {
      layerCounts.push({
        color: getSeriesChannelCount(allSeries, index, 'color'),
        size: getSeriesChannelCount(allSeries, index, 'size'),
        detail: getSeriesChannelCount(allSeries, index, 'detail'),
        text: getSeriesChannelCount(allSeries, index, 'text'),
        angle: getSeriesChannelCount(allSeries, index, 'angle'),
        baseline: getSeriesChannelCount(allSeries, index, 'baseline'),
        label: undefined,
        tooltip: undefined,
      });
    });
  }
  dataStats.layers = layerCounts;
  return dataStats;
}

function getFieldFromRecord(field: EncField, record: QueryResult.Item) {
  if (!field.id || record[field.id] === null) {
    return null;
  } else {
    return record[field.id];
  }
}

function makeTransforms(seriesFields: Array<EncField>): any {
  const transforms: Record<string, (dataset: any) => void> = {};
  const DataModel = muze.DataModel;
  const { EQUAL } = DataModel.ComparisonOperators;

  for (const seriesField of seriesFields) {
    if (!seriesField.id) {
      continue;
    }
    transforms[seriesField.id] = (dataset) => {
      return dataset.select({
        field: SID_FIELD,
        operator: EQUAL,
        value: seriesField.id,
      });
    };
  }

  return transforms;
}
